import { useTranslation } from "@next-platform/client-common-core";
import { Modal } from "@next-platform/client-common-spicy-ui";
import { Checkbox } from "@nextui-org/react";
import { ComponentProps, JSX, useState } from "react";

import { Col, Row, SpicyButton, Text } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";

type ConfirmationModalProps = {
  handleClose: () => void;
  onUserConfirmation: (keepPinnedMemories: boolean, keepManualEntries: boolean) => Promise<void>;
  confirmationMessage?: string;
  isVisible: boolean;
  isLoading: boolean;
  actionButtonColor?: ComponentProps<typeof SpicyButton>["color"];
  actionButtonText?: string;
  discardButtonText?: string;
  showButtons?: boolean;
  handleOnClose?: () => void;
  dataTestId?: string;
};

function ConfirmationModal({
  onUserConfirmation,
  isLoading = false,
  handleClose,
  handleOnClose,
  isVisible,
  showButtons = true,
  actionButtonColor = "primary_strong",
  actionButtonText,
  discardButtonText,
  dataTestId = "",
}: ConfirmationModalProps): JSX.Element {
  const { t } = useTranslation();
  const [keepPinnedMemories, setKeepPinnedMemories] = useState(false);
  const [keepManualEntries, setKeepManualEntries] = useState(false);
  const [error, setError] = useState("");

  const onUserConfirmationClickHandler = async () => {
    try {
      setError("");

      await onUserConfirmation(keepPinnedMemories, keepManualEntries);
      if (!error) {
        handleClose();
      }
    } catch (error) {
      setError((error as Error).message);
    }
  };

  const onClose = () => {
    if (handleOnClose) {
      handleOnClose();
    } else {
      setError("");
      handleClose();
    }
  };

  return (
    <Modal
      title={t("chat:modal.deleteAllMemories.modalTitle")}
      classNames={{ title: "text-center pl-[28px]" }}
      backdrop="blur"
      dismissable={!isLoading}
      open={isVisible}
      onOpenChange={onClose}
    >
      <Col className="text-center" dataTestId={dataTestId}>
        <Text variant={"md"} type={"normal"}>
          <Trans i18nKey="chat:modal.deleteAllMemories.title" />
        </Text>
        {error ? (
          <Text variant={"xs"} type={"error"}>
            {error}
          </Text>
        ) : null}
      </Col>
      <Row className="pl-10">
        <Checkbox size="sm" isSelected={keepPinnedMemories} onValueChange={setKeepPinnedMemories}>
          <Trans i18nKey="chat:modal.deleteAllMemories.checkboxLabel.keepPinnedMemories" />
        </Checkbox>
      </Row>

      <Row className="pl-10">
        <Checkbox size="sm" isSelected={keepManualEntries} onValueChange={setKeepManualEntries}>
          <Trans i18nKey="chat:modal.deleteAllMemories.checkboxLabel.keepManualEntries" />
        </Checkbox>
      </Row>
      <Row>
        <Text variant="xs" type="neutral">
          <Trans i18nKey="chat:modal.deleteAllMemories.description" />
        </Text>
      </Row>
      {showButtons ? (
        <Row className="gap-2 justify-center">
          <SpicyButton isDisabled={isLoading} variant="secondary_small" color="primary_light" onPress={handleClose}>
            {discardButtonText ? discardButtonText : <Trans i18nKey="common:cancel" />}
          </SpicyButton>
          <SpicyButton isDisabled={isLoading} variant="secondary_small" color={actionButtonColor} onPress={onUserConfirmationClickHandler}>
            {isLoading ? (
              <Trans i18nKey="chat:modal.deleteAllMemories.loading" />
            ) : (
              actionButtonText || <Trans i18nKey="chat:modal.deleteAllMemories.cta.deleteAll" />
            )}
          </SpicyButton>
        </Row>
      ) : null}
    </Modal>
  );
}

export default ConfirmationModal;
